import { useCallback, useMemo } from 'react';

import DefaultFacet from './DefaultFacet';

/**
 * ContentHubFacet
 * Ensures the mandatory Content Hub facets (effective date + content type) are always present
 * and cannot be removed from the block configuration.
 */
const ContentHubFacet = (props) => {
  const DEFAULT_TYPES = useMemo(() => ['Audio', 'Image', 'Video'], []);

  const ensurePortalType = useCallback(
    (facets) => {
      const next = { ...(facets || {}) };

      const isEmpty =
        !next.portal_type ||
        (Array.isArray(next.portal_type) && next.portal_type.length === 0);

      if (isEmpty) {
        next.portal_type = DEFAULT_TYPES;
      }

      return next;
    },
    [DEFAULT_TYPES],
  );

  const wrappedOnTriggerSearch = useCallback(
    (text, facets, sortOn, sortOrder) => {
      props.onTriggerSearch(text, ensurePortalType(facets), sortOn, sortOrder);
    },
    [ensurePortalType, props],
  );

  const wrappedSetFacets = useCallback(
    (facets) => {
      props.setFacets(ensurePortalType(facets));
    },
    [ensurePortalType, props],
  );

  const hasNonDefaultFilters = useMemo(() => {
    const entries = Object.entries(props.facets || {});
    if (!entries.length) return false;

    return entries.some(([field, value]) => {
      const values = Array.isArray(value)
        ? value
        : value?.values || value?.value || [];

      const normalized = Array.isArray(values) ? values.map(String).sort() : [];

      if (field === 'portal_type') {
        if (normalized.length !== DEFAULT_TYPES.length) return true;
        return !normalized.every((v, i) => v === DEFAULT_TYPES[i]);
      }

      return normalized.length > 0;
    });
  }, [props.facets, DEFAULT_TYPES]);

  const defaultFacets = useMemo(
    () => ({ portal_type: DEFAULT_TYPES }),
    [DEFAULT_TYPES],
  );

  return (
    <DefaultFacet
      {...props}
      setFacets={wrappedSetFacets}
      onTriggerSearch={wrappedOnTriggerSearch}
      filterListVisible={hasNonDefaultFilters}
      defaultFacets={defaultFacets}
    />
  );
};

export default ContentHubFacet;
